import { memo, useCallback, useEffect } from 'react';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  Easing,
  withDelay,
  withSequence,
} from 'react-native-reanimated';
import { runOnJS } from 'react-native-worklets';
import { COIN_SIZE, DEFAULT_ENTRY_POINT } from './AnimatedCoin.content';
import { randomInRange } from './AnimatedCoin.utils';
import { AnimatedCoinProps } from './AnimatedCoin.types';
import Image from '../../Image/Image';
import AnimatedBurst from '../AnimatedBurst/AnimatedBurst';

function AnimatedCoin({
  index,
  entryPoint = DEFAULT_ENTRY_POINT,
  targetPoint,
  duration = 2000,
  spawnRange = 30,
}: AnimatedCoinProps) {
  const opacity = useSharedValue(0);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => ({
    position: 'absolute',
    top: entryPoint.y,
    left: entryPoint.x,
    opacity: opacity.value,
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
    ],
  }));
  const burstScale = useSharedValue(0.5);
  const burstOpacity = useSharedValue(1);
  const burstAnimatedStyle = useAnimatedStyle(() => ({
    opacity: burstOpacity.value,
    transform: [
      { scale: burstScale.value },
      { translateX: (-COIN_SIZE / 2) * (1 / burstScale.value) },
      { translateY: (-COIN_SIZE / 2) * (1 / burstScale.value) },
    ],
  }));

  const performEntryAnimation = useCallback(
    () =>
      new Promise<void>((resolve) => {
        let completedAnimations = 0;

        const animationDelay = index * 50;
        const animationConfig = {
          duration: duration * 0.15,
          easing: Easing.bezier(0.68, 0, 0.32, 1),
        };
        const randomX = randomInRange(spawnRange * -1, spawnRange);
        const randomY = randomInRange(spawnRange * -1, spawnRange);
        const burstAnimationDelay = 50 * index + duration * 0.15;
        const burstAnimationConfig = {
          duration: duration * 0.08,
          easing: Easing.out(Easing.ease),
        };

        const onComplete = () => {
          completedAnimations++;

          if (completedAnimations === animations.length) {
            resolve();
          }
        };

        const animations = [
          (opacity.value = withDelay(
            animationDelay,
            withTiming(1, animationConfig, () => {
              runOnJS(onComplete)();
            }),
          )),
          (translateX.value = withDelay(
            animationDelay,
            withTiming(-(COIN_SIZE / 2) + randomX, animationConfig, () => {
              runOnJS(onComplete)();
            }),
          )),
          (translateY.value = withDelay(
            animationDelay,
            withTiming(-(COIN_SIZE / 2) + randomY, animationConfig, () => {
              runOnJS(onComplete)();
            }),
          )),
          (burstScale.value = withDelay(
            burstAnimationDelay,
            withTiming(1, burstAnimationConfig, () => {
              runOnJS(onComplete)();
            }),
          )),
          (burstOpacity.value = withDelay(
            burstAnimationDelay,
            withTiming(0, burstAnimationConfig, () => {
              runOnJS(onComplete)();
            }),
          )),
        ];
      }),
    [
      index,
      duration,
      spawnRange,
      opacity,
      translateX,
      translateY,
      burstScale,
      burstOpacity,
    ],
  );

  const performFlyingAnimation = useCallback(
    async () =>
      new Promise<void>((resolve) => {
        let completedAnimations = 0;

        const animationConfig = {
          duration: duration * 0.7,
          easing: Easing.bezier(0.68, -0.3, 0.32, 1),
        };

        const onComplete = () => {
          completedAnimations++;

          if (completedAnimations === animations.length) {
            resolve();
          }
        };

        const animations = [
          (translateX.value = withTiming(
            -(COIN_SIZE / 2) + (targetPoint.x - entryPoint.x),
            animationConfig,
            () => {
              runOnJS(onComplete)();
            },
          )),
          (translateY.value = withTiming(
            -(COIN_SIZE / 2) + (targetPoint.y - entryPoint.y),
            animationConfig,
            () => {
              runOnJS(onComplete)();
            },
          )),
        ];
      }),
    [duration, entryPoint, targetPoint, translateX, translateY],
  );

  const performExitAnimation = useCallback(
    async () =>
      new Promise<void>((resolve) => {
        const animationConfig = {
          duration: duration * 0.15,
          easing: Easing.out(Easing.ease),
        };
        const burstAnimationConfig = {
          duration: duration * 0.08,
          easing: Easing.out(Easing.ease),
        };

        opacity.value = withTiming(0, animationConfig, () => {
          runOnJS(resolve)();
        });
        burstOpacity.value = withSequence(
          withTiming(1, burstAnimationConfig),
          withTiming(1, burstAnimationConfig),
          withTiming(0, burstAnimationConfig),
        );
        burstScale.value = withSequence(
          withTiming(0.75, burstAnimationConfig),
          withTiming(0.75, burstAnimationConfig),
          withTiming(1.5, burstAnimationConfig),
        );
      }),
    [duration, opacity, burstOpacity, burstScale],
  );

  const animate = useCallback(async () => {
    await performEntryAnimation();
    await performFlyingAnimation();
    await performExitAnimation();
  }, [performEntryAnimation, performFlyingAnimation, performExitAnimation]);

  useEffect(() => {
    animate();
  }, [animate]);

  return (
    <Animated.View style={animatedStyle}>
      <AnimatedBurst index={index} animatedStyle={burstAnimatedStyle} />
      <Image name="common.RotatingCoin" width={COIN_SIZE} height={COIN_SIZE} />
    </Animated.View>
  );
}

export default memo(AnimatedCoin);
